import { useEffect, useState } from "react";
import { Popconfirm, Tooltip, message } from "antd";
import { CareShell, Panel } from "../../components/care/CareLayout";
import { useAuth } from "../../contexts/AuthContext";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import { careService } from "../../services/careService";
import GenericEvidenceForm from "../../components/care/GenericEvidenceForm";

const EVIDENCE_TYPE = "WA_AFH_075_HOT_WATER_TEMPERATURE";

const pageItems = (payload) =>
  Array.isArray(payload)
    ? payload
    : payload?.content || payload?.data || payload?.items || [];

const currentUserId = (user = {}) =>
  user?.id || user?.userId || user?.uid || "";

const formatDateTime = (value) => {
  if (!value) return "Not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not set";
  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const locationLabel = (value) =>
  ({
    RESIDENT_BATHROOM: "Resident bathroom",
    KITCHEN_SINK: "Kitchen sink",
    LAUNDRY: "Laundry",
    OTHER: "Other",
  })[value] ||
  value ||
  "-";

const statusClass = (status) =>
  String(status).toUpperCase() === "OUT_OF_RANGE" ? "high" : "low";

export default function WaterTemperatureReadingsPage() {
  const { user } = useAuth();
  const { selectedWorkspaceId, workspaceListReady } = useWorkspace();
  const actorId = currentUserId(user);
  const workspaceParams = {
    wid: selectedWorkspaceId,
    workspaceId: selectedWorkspaceId,
  };

  const [readings, setReadings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("CREATE"); // CREATE | EDIT | VIEW
  const [activeReading, setActiveReading] = useState(null);
  const [deletingId, setDeletingId] = useState("");

  const loadReadings = () => {
    if (!workspaceListReady || !selectedWorkspaceId) {
      setReadings([]);
      return;
    }
    setLoading(true);
    setError("");
    careService.waterTemperatureReadings
      .list(workspaceParams)
      .then((data) => setReadings(pageItems(data)))
      .catch((err) => {
        setReadings([]);
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Unable to load hot water temperature readings.",
        );
      })
      .finally(() => setLoading(false));
  };

  useEffect(loadReadings, [selectedWorkspaceId, workspaceListReady]); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => {
    setActiveReading(null);
    setDrawerMode("CREATE");
    setDrawerOpen(true);
  };

  const openView = (reading) => {
    setActiveReading(reading);
    setDrawerMode("VIEW");
    setDrawerOpen(true);
  };

  const openEdit = (reading) => {
    setActiveReading(reading);
    setDrawerMode("EDIT");
    setDrawerOpen(true);
  };

  const handleSaved = () => {
    setDrawerOpen(false);
    setActiveReading(null);
    loadReadings();
  };

  const deleteReading = async (reading) => {
    if (!reading?.id) return;
    setDeletingId(reading.id);
    try {
      await careService.waterTemperatureReadings.delete(reading.id, {
        actorId,
        ...workspaceParams,
      });
      setReadings((current) =>
        current.filter((item) => item.id !== reading.id),
      );
      message.success("Reading deleted.");
    } catch (err) {
      message.error(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to delete reading.",
      );
    } finally {
      setDeletingId("");
    }
  };

  const metrics = {
    total: readings.length,
    latest: readings[0]?.readingDateTime,
    outOfRange: readings.filter(
      (reading) => String(reading.status).toUpperCase() === "OUT_OF_RANGE",
    ).length,
  };

  return (
    <CareShell className="dashboard-content water-temperature-readings-page emergency-evacuation-drills-page">
      <div className="residents-page-head">
        <div className="page-title">
          <h1>Hot Water Temperature Readings</h1>
          <p>
            Log and review resident-accessible hot water temperature checks
            (required range: 105°F–120°F).
          </p>
        </div>
        <button
          className="mini-btn emergency-evacuation-schedule-button"
          type="button"
          onClick={openNew}
        >
          <i className="ti ti-thermometer" /> Log Reading
        </button>
      </div>

      <div className="medication-summary-grid emergency-evacuation-drill-summary-grid">
        <Panel title="Total readings">
          <div className="resident-stat-value">{metrics.total}</div>
        </Panel>
        <Panel title="Last reading">
          <div className="resident-stat-value compact">
            {formatDateTime(metrics.latest)}
          </div>
        </Panel>
        <Panel title="Out of range">
          <div
            className={`resident-stat-value${metrics.outOfRange ? " text-danger" : ""}`}
          >
            {metrics.outOfRange}
          </div>
        </Panel>
      </div>

      <Panel title="Reading Log">
        {error ? <p className="form-error">{error}</p> : null}
        <div className="medication-table-wrap">
          <table className="medication-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Location</th>
                <th>Temperature</th>
                <th>Status</th>
                <th>Notes</th>
                <th style={{textAlign:"right"}}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: "center" }}>
                    Loading readings...
                  </td>
                </tr>
              ) : null}
              {!loading && !readings.length ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: "center" }}>
                    No readings logged yet.
                  </td>
                </tr>
              ) : null}
              {!loading &&
                readings.map((reading) => (
                  <tr key={reading.id}>
                    <td>{formatDateTime(reading.readingDateTime)}</td>
                    <td>{locationLabel(reading.location)}</td>
                    <td>{reading.temperatureFahrenheit}°F</td>
                    <td>
                      <em className={`pill ${statusClass(reading.status)}`}>
                        {reading.status || "IN_RANGE"}
                      </em>
                    </td>
                    <td>{reading.notes || "-"}</td>
                    <td>
                      <div className="medication-row-actions">
                        <Tooltip title="Details">
                          <button
                            className="resident-icon-button"
                            type="button"
                            aria-label="View reading details"
                            onClick={() => openView(reading)}
                          >
                            <i className="ti ti-eye" />
                          </button>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <button
                            className="resident-icon-button"
                            type="button"
                            aria-label="Edit reading"
                            onClick={() => openEdit(reading)}
                          >
                            <i className="ti ti-edit" />
                          </button>
                        </Tooltip>
                        <Popconfirm
                          title="Delete reading?"
                          description="This record will be removed from the log."
                          okText="Delete"
                          okButtonProps={{
                            danger: true,
                            loading: deletingId === reading.id,
                          }}
                          onConfirm={() => deleteReading(reading)}
                        >
                          <Tooltip title="Delete">
                            <button
                              className="resident-icon-button danger"
                              type="button"
                              aria-label="Delete reading"
                            >
                              <i className="ti ti-trash" />
                            </button>
                          </Tooltip>
                        </Popconfirm>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <GenericEvidenceForm
        open={drawerOpen}
        evidenceType={EVIDENCE_TYPE}
        workspaceId={selectedWorkspaceId}
        actorId={actorId}
        record={drawerMode === "CREATE" ? null : activeReading}
        readOnly={drawerMode === "VIEW"}
        onClose={() => setDrawerOpen(false)}
        onSaved={handleSaved}
      />
    </CareShell>
  );
}
